import { addMinutes, addYears, differenceInMinutes, parseISO } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { getRenewalDispatches } from "../../subscription-product/utils/getRenewalDispatches.js";
import { isSubscriptionShapeBookClub, isSubscriptionShapeSeries, isSubscriptionShapeSubscription, } from "../../subscription-plan/selectors/isSubscriptionShape.js";
// Shared helpers
const isMoreThanOneHourUntil = (date, now) => differenceInMinutes(date, now) > 60;
const renewIn50Years = (base) => addYears(base, 50).toISOString();
const renewIn15Minutes = (base) => addMinutes(base, 15).toISOString();
// Series shape: honor subscriptionPublicationDate, then publicationDate, else 15 minutes
export const resolveNextRenewAtForSeries = (nextBook) => {
    const now = new Date();
    if (nextBook) {
        const subscriptionPublicationDate = nextBook.subscriptionPublicationDate
            ? new Date(nextBook.subscriptionPublicationDate)
            : undefined;
        const publicationDate = nextBook.publicationDate
            ? new Date(nextBook.publicationDate)
            : undefined;
        if (subscriptionPublicationDate && subscriptionPublicationDate > now) {
            return subscriptionPublicationDate.toISOString();
        }
        if (!subscriptionPublicationDate &&
            publicationDate &&
            publicationDate > now) {
            return publicationDate.toISOString();
        }
    }
    return renewIn15Minutes(now);
};
const parseBookClubDate = (value) => {
    const normalized = value.trim().replace(" ", "T");
    const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
    return hasTimeZone
        ? parseISO(normalized)
        : fromZonedTime(normalized, "Europe/Oslo");
};
export class BookClubDispatchError extends Error {
    code;
    constructor(code, detail) {
        super(detail);
        this.code = code;
        this.name = "BookClubDispatchError";
    }
}
/** Select the earliest shipment whose member extraction is still in the future. */
export const resolveNextRenewAtForBookClub = (hasGifts, dispatches, { now = new Date(), productSku } = {}) => {
    if (hasGifts) {
        return {
            renewAt: renewIn15Minutes(now),
            isGiftRenewal: true,
        };
    }
    const selected = (dispatches ?? [])
        .flatMap((dispatch, index) => {
        const context = `sku: ${productSku ?? "unknown"}, batchCode: ${dispatch.batchCode ?? "missing"}, dispatch index: ${index}`;
        if (!dispatch.distributionDate ||
            !Number.isFinite(parseBookClubDate(dispatch.distributionDate).getTime())) {
            throw new BookClubDispatchError("INVALID_CONFIGURATION", `Invalid book club configuration: missing or invalid distributionDate; ${context}`);
        }
        const distributionDate = parseBookClubDate(dispatch.distributionDate);
        // Historical dispatches do not need to be repaired to allow new signups.
        if (distributionDate <= now)
            return [];
        if (!dispatch.memberExtractionDate ||
            !Number.isFinite(parseBookClubDate(dispatch.memberExtractionDate).getTime())) {
            throw new BookClubDispatchError("INVALID_CONFIGURATION", `Invalid book club configuration: missing or invalid memberExtractionDate; ${context}`);
        }
        const memberExtractionDate = parseBookClubDate(dispatch.memberExtractionDate);
        return memberExtractionDate > now
            ? [{ dispatch, distributionDate, renewAt: dispatch.distributionDate }]
            : [];
    })
        .sort((a, b) => a.distributionDate.getTime() - b.distributionDate.getTime())[0];
    if (!selected) {
        throw new BookClubDispatchError("NO_ELIGIBLE_DISPATCH", `No eligible book club dispatch for signup; sku: ${productSku ?? "unknown"}`);
    }
    return {
        renewAt: selected.renewAt,
        selectedDispatch: selected.dispatch,
        isGiftRenewal: false,
    };
};
// Book subscription shape and book club shape
export const resolveNextRenewAtForBookSubscriptionDetails = (hasGifts, dispatches) => {
    const now = new Date();
    if (hasGifts) {
        // With gifts, renew quickly (15 minutes)
        return {
            renewAt: renewIn15Minutes(now),
            isGiftRenewal: true,
        };
    }
    if (!dispatches || dispatches.length === 0) {
        // No dispatch table configured -> far future fallback
        return {
            renewAt: renewIn50Years(now),
            isGiftRenewal: false,
        };
    }
    // Use helper to get current/next dispatch relative to now
    const { current, next } = getRenewalDispatches(dispatches);
    if (!current?.distributionDate) {
        // No current dispatch could be resolved
        return {
            renewAt: renewIn50Years(now),
            isGiftRenewal: false,
        };
    }
    const currentDate = new Date(current.distributionDate);
    // For book club: if signup is after the member extraction date, skip this dispatch
    // and assign to the next one. The member list is locked at extraction, so late
    // signups are not part of this dispatch's membership magazine.
    if (current.memberExtractionDate) {
        const memberExtractionDate = new Date(current.memberExtractionDate);
        if (now > memberExtractionDate && next?.distributionDate) {
            return {
                renewAt: next.distributionDate,
                selectedDispatch: next,
                isGiftRenewal: false,
            };
        }
    }
    // The 1-hour buffer is evaluated from the raw distributionDate value.
    // Datetime values use their exact timestamp, while date-only values are
    // interpreted as midnight in this check.
    if (isMoreThanOneHourUntil(currentDate, now)) {
        return {
            renewAt: current.distributionDate,
            selectedDispatch: current,
            isGiftRenewal: false,
        };
    }
    // Current dispatch is too close; use the next one if available
    if (next?.distributionDate) {
        return {
            renewAt: next.distributionDate,
            selectedDispatch: next,
            isGiftRenewal: false,
        };
    }
    // No suitable dispatch found
    return {
        renewAt: renewIn50Years(now),
        isGiftRenewal: false,
    };
};
export const resolveNextRenewAtForBookSubscription = (hasGifts, dispatches) => {
    return resolveNextRenewAtForBookSubscriptionDetails(hasGifts, dispatches)
        .renewAt;
};
export const resolveRenewAtContext = (type, nextBook, hasGifts, dispatches) => {
    if (isSubscriptionShapeSeries(type)) {
        return {
            renewAt: resolveNextRenewAtForSeries(nextBook),
        };
    }
    if (isSubscriptionShapeSubscription(type)) {
        return {
            renewAt: resolveNextRenewAtForBookSubscription(hasGifts, dispatches),
        };
    }
    if (isSubscriptionShapeBookClub(type)) {
        const bookClubRenewAt = resolveNextRenewAtForBookClub(hasGifts, dispatches);
        return {
            renewAt: bookClubRenewAt.renewAt,
            bookSubscriptionRenewContext: bookClubRenewAt,
        };
    }
    throw new Error(`Error resolving next renewAt. Unknown subscription type: ${type}`);
};
/**
 * Orchestrator that routes to the correct resolver by subscription shape
 */
export const resolveNextRenewAt = (type, nextBook, hasGifts, dispatches) => {
    return resolveRenewAtContext(type, nextBook, hasGifts, dispatches).renewAt;
};
